/**
 * EditableTable - Notion-style table with inline editing
 * Compact CRM style with hover states and click-to-edit
 */

import { ReactNode } from 'react';
import { EditableCell, type EditableCellProps } from './EditableCell';
import { cn } from '../../lib/utils';
import { tableStyles } from '../../lib/styles';

export interface Column<T> {
  /** Column identifier */
  key: string;
  /** Column header text */
  header: string;
  /** Width (CSS value) */
  width?: string;
  /** Whether column is sortable */
  sortable?: boolean;
  /** Cell renderer (for non-editable cells) */
  render?: (item: T, index: number) => ReactNode;
  /** Editable configuration (makes cell editable) */
  editable?: {
    type?: EditableCellProps['type'];
    options?: EditableCellProps['options'];
    placeholder?: string;
    formatter?: EditableCellProps['formatter'];
    /** Get the field value from item */
    getValue?: (item: T) => string | number | null | undefined;
    /** Whether cell is editable for this item */
    isEditable?: (item: T) => boolean;
  };
  /** Cell class name */
  cellClassName?: string;
  /** Header class name */
  headerClassName?: string;
}

export interface EditableTableProps<T> {
  /** Column definitions */
  columns: Column<T>[];
  /** Data array */
  data: T[];
  /** Get unique key for each row */
  getRowKey: (item: T) => string;
  /** Handle inline edit save */
  onSave: (item: T, field: string, value: string) => Promise<void>;
  /** Handle inline edit error */
  onError?: (item: T, field: string, error: Error) => void;
  /** Loading state */
  loading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Table class name */
  className?: string;
  /** Row click handler */
  onRowClick?: (item: T) => void;
  /** Selected row key */
  selectedRowKey?: string;
  /** Sort state */
  sortBy?: string;
  /** Sort direction */
  sortDirection?: 'asc' | 'desc';
  /** Sort handler */
  onSort?: (key: string) => void;
}

export function EditableTable<T>({
  columns,
  data,
  getRowKey,
  onSave,
  onError,
  loading = false,
  emptyMessage = 'No hay datos',
  className,
  onRowClick,
  selectedRowKey,
  sortBy,
  sortDirection,
  onSort,
}: EditableTableProps<T>) {
  const handleCellSave = async (item: T, field: string, value: string) => {
    await onSave(item, field, value);
  };

  const handleCellError = (item: T, field: string, error: Error) => {
    onError?.(item, field, error);
  };

  if (loading) {
    return (
      <div className={cn(tableStyles.container, className)}>
        <div className="p-4 text-center">
          <div className="animate-pulse flex flex-col items-center gap-1.5">
            <div className="h-2.5 w-24 bg-gray-100 rounded" />
            <div className="h-2.5 w-20 bg-gray-100 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn(tableStyles.container, className)}>
        <div className="p-4 text-center text-xs text-gray-500">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className={cn(tableStyles.container, className)}>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className={tableStyles.headerRow}>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    column.sortable ? tableStyles.headerCellSortable : tableStyles.headerCell,
                    column.headerClassName
                  )}
                  style={{ width: column.width }}
                  onClick={column.sortable && onSort ? () => onSort(column.key) : undefined}
                >
                  <div className="flex items-center gap-1">
                    {column.header}
                    {column.sortable && sortBy === column.key && (
                      <span className="text-blue-500 text-[10px]">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={tableStyles.body}>
            {data.map((item, rowIndex) => {
              const rowKey = getRowKey(item);
              const isSelected = selectedRowKey === rowKey;

              return (
                <tr
                  key={rowKey}
                  className={cn(
                    tableStyles.row,
                    isSelected && tableStyles.rowSelected,
                    onRowClick && tableStyles.rowClickable
                  )}
                  onClick={onRowClick ? () => onRowClick(item) : undefined}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        tableStyles.cell,
                        column.cellClassName
                      )}
                      onDoubleClick={(e) => {
                        // Stop propagation on double-click to allow inline editing
                        if (column.editable) {
                          e.stopPropagation();
                        }
                      }}
                    >
                      {column.editable ? (
                        <EditableCell
                          value={
                            column.editable.getValue
                              ? column.editable.getValue(item)
                              : (item as Record<string, unknown>)[column.key] as string | number | null
                          }
                          onSave={(value) => handleCellSave(item, column.key, value)}
                          onError={(error) => handleCellError(item, column.key, error)}
                          type={column.editable.type}
                          options={column.editable.options}
                          placeholder={column.editable.placeholder}
                          formatter={column.editable.formatter}
                          disabled={
                            column.editable.isEditable
                              ? !column.editable.isEditable(item)
                              : false
                          }
                        />
                      ) : column.render ? (
                        column.render(item, rowIndex)
                      ) : (
                        String((item as Record<string, unknown>)[column.key] ?? '—')
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Export EditableCell for standalone use
 */
export { EditableCell };
export type { EditableCellProps };
